import joi from 'joi';
import { Record as record } from 'immutable';
import _ from 'lodash';

import { translate as t } from "../../lib/I18n";
import { PaymentType } from '../../constants/AppConstants';
import { construct, fields, resolve, resolveAll } from '../../helpers/LinkMapper';
import { getDefaults, getResolvers } from '../../helpers/Records';
import ValidationMixin, { getValues } from '../ValidationMixin';
import VoucherForm from './VoucherForm';
import CardForm from './CardForm';
import ContactForm from './ContactForm';

const schema = {
    vouchers: [resolve.as(VoucherForm), new VoucherForm()],
    card: [resolve.as(CardForm), new CardForm()],
    contact: [resolve.as(ContactForm), new ContactForm()],
    paymentMethod: [fields.string, null],
    isLoggedIn: [fields.boolean, false],
    visaCards: [resolveAll.with(Object)],
    discountCode: [fields.string, ""],
};

class PaymentForm extends record(getDefaults(schema)) {

    static fromJSON (json = {}) {
        return construct(PaymentForm, _.defaults(json, getDefaults(schema)), getResolvers(schema));
    }

    getComputedValues () {
        const values = this.getPaymentRecords().map(getValues);
        return Object.assign({}, ...values, this.contact.toJS());
    }

    getSchema () {
        let schemas = this.getPaymentRecords().map((form) => form.getSchema());

        if (!this.isLoggedIn && this.isCardPayment() && this.requiresPayment()) {
            schemas.push(this.contact.getSchema());
        }

        return Object.assign({
            paymentMethod: joi.string().required(),
            discountCode: joi.string().allow('').trim().optional().min(3).max(100).options({
                language: {
                    any: {
                        empty: t('alertmessage.badDiscountCode')
                    },
                    string: {
                        base: t('alertmessage.badDiscountCode'),
                        min: t('alertmessage.badDiscountCode'),
                        max: t('alertmessage.badDiscountCode')
                    }
                }
            }),
        }, ...schemas);
    }

    debugPrintCard() {
        return this.card.debugPrint();
    }

    canUseVouchers () {
        return !!this.vouchers.vouchersTotal;
    }

    hasVoucherPayment () {
        return this.getVoucherAmount() > 0;
    }

    getVoucherAmount () {
        return this.canUseVouchers() && this.vouchers.useVouchers ? this.vouchers.voucherAmountPence : 0;
    }

    getPreAuthVoucherAmount () {
        return this.requiresPreAuthVouchers() ? this.getVoucherAmount() : 0;
    }

    requiresPayment () {
        return this.getPaymentAmount() > 0;
    }

    requiresPreAuthVouchers () {
        if (this.hasVoucherPayment() && this.getPaymentAmount() === 0) {
            // must NOT pre-auth if the payment is voucher-only
            return false;
        } else if (!this.isCardPayment() && !this.isVisaCheckout()) {
            // All payment methods _except_ Pay360 API & Visa Checkout support pre-auth
            return true;
        }
        return false;
    }

    getPaymentAmount () {
        const { voucherAmountPence, basketTotal } = this.vouchers;
        if (this.hasVoucherPayment()) {
            const amount = basketTotal - voucherAmountPence;
            return Math.max(0, amount);
        } else {
            return basketTotal;
        }
    }

    isAmazonPay () {
        return this.paymentMethod === PaymentType.AMAZON_PAY;
    }

    isPayPal () {
        return this.paymentMethod === PaymentType.PAYPAL;
    }

    isCardPayment () {
        return this.paymentMethod === PaymentType.CARD;
    }

    isVisaCheckout () {
        return this.paymentMethod === PaymentType.VISA_CHECKOUT;
    }

    isPay360Hosted () {
        return this.paymentMethod === PaymentType.PAY360_HOSTED;
    }

    isBraintreeCardPayment () {
        return this.paymentMethod === PaymentType.BRAINTREE_CARD_CHECKOUT;
    }

    hasStoredCard () {
        return this.isCardPayment() && this.card.storedCard;
    }

    getPaymentRecords () {
        let methods = [];
        if (this.hasVoucherPayment()) {
            methods.push(this.vouchers);
        }
        if (this.isCardPayment() && this.requiresPayment()) {
            methods.push(this.card);
        }
        return methods;
    }

    setAddress (address) {
        return this.set('card', this.card.setAddress(address));
    }

    getVouchersTotal () {
        return this.getIn(['vouchers', 'total']);
    }

    setVouchersTotal (total, preAuthedAmount) {
        return this.set('vouchers', this.vouchers.setVouchersTotal(total, preAuthedAmount));
    }

    setBasketTotal (total) {
        return this.set('vouchers', this.vouchers.setBasketTotal(total));
    }

    get basketTotal () {
        return this.getIn(['vouchers', 'basketTotal'], 0);
    }

    setContactDetails (email, phone) {
        return this.set('contact', this.contact.setContactDetails(email, phone));
    }

    hasPendingDiscountCode() {
        // discountCode is emptied after it is applied
        return _.trim(this.discountCode).length > 0;
    }
}


Object.assign(PaymentForm.prototype, ValidationMixin);


export default PaymentForm;



// WEBPACK FOOTER //
// ./src/records/payment/Form.js